/** Sidebar tree: My Tasks / Inbox / Calendar, Favorites, Spaces → Folders → Lists with open counts. */
import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Archive,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  Folder as FolderIcon,
  FolderPlus,
  Inbox,
  ListChecks,
  ListPlus,
  MoreHorizontal,
  Palette,
  Pencil,
  Plus,
  Star,
  Tags,
  Trash2,
  CircleDot,
  FolderInput,
} from 'lucide-react';
import { isCompleteType, SPACE_COLORS, type Folder, type List, type Space } from '@karya/core';
import { cn } from '@/lib/cn';
import { useAllTasks, useUnreadCount, useWorkspace } from '@/data/queries';
import * as m from '@/data/mutations';
import { usePrefs } from '@/state/ui';
import { IconButton } from '@/components/ui/button';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from '@/components/ui/menu';
import { openDialog } from './dialogs';
import { openStatusEditor, openTagsEditor } from './editor-store';

export function useOpenCounts(): Map<string, number> {
  const tasks = useAllTasks();
  const ws = useWorkspace();
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of tasks ?? []) {
      if (t.archived_at || t.parent_task_id) continue;
      if (isCompleteType(ws.statusById.get(t.status_id)?.type)) continue;
      counts.set(t.list_id, (counts.get(t.list_id) ?? 0) + 1);
    }
    return counts;
  }, [tasks, ws]);
}

const itemCls =
  'group/item flex h-7 items-center gap-2 rounded-sm px-2 text-base whitespace-nowrap';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const ws = useWorkspace();
  const counts = useOpenCounts();
  const unread = useUnreadCount();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const favorites = usePrefs((s) => s.favorites);
  const setCollapsed = usePrefs((s) => s.setSidebarCollapsed);
  const activeListId = /^\/l\/([^/]+)/.exec(path)?.[1];
  const favLists = favorites.map((id) => ws.listById.get(id)).filter((l): l is List => Boolean(l));

  return (
    <aside
      className="flex h-full w-[232px] shrink-0 flex-col border-r border-line bg-surface-1"
      aria-label="Spaces and lists"
    >
      <div className="flex h-11 items-center gap-2 px-3">
        <span className="flex size-[22px] items-center justify-center rounded-[6px] bg-primary text-cap font-bold text-on-primary">
          K
        </span>
        <span className="flex-1 font-semibold">Karya</span>
        <IconButton label="Collapse sidebar" size="sm" onClick={() => setCollapsed(true)}>
          <ChevronsLeft className="size-4" />
        </IconButton>
      </div>
      <nav className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto px-2 pb-4 scroll-thin">
        <NavLink
          to="/"
          icon={<ListChecks className="size-4" />}
          label="My Tasks"
          active={path === '/'}
          onNavigate={onNavigate}
        />
        <NavLink
          to="/inbox"
          icon={<Inbox className="size-4" />}
          label="Inbox"
          count={unread || undefined}
          active={path.startsWith('/inbox')}
          onNavigate={onNavigate}
        />
        <NavLink
          to="/calendar"
          icon={<CalendarDays className="size-4" />}
          label="Calendar"
          active={path === '/calendar'}
          onNavigate={onNavigate}
        />

        {favLists.length > 0 && (
          <>
            <SectionTitle>Favorites</SectionTitle>
            {favLists.map((l) => (
              <ListItem
                key={l.id}
                list={l}
                count={counts.get(l.id)}
                active={l.id === activeListId}
                indent={0}
                prefix={ws.spaceById.get(l.space_id)?.name}
                onNavigate={onNavigate}
              />
            ))}
          </>
        )}

        <SectionTitle
          action={
            <IconButton
              label="New space"
              size="sm"
              onClick={() =>
                openDialog({
                  kind: 'name',
                  title: 'New space',
                  label: 'Space name',
                  submit: 'Create space',
                  onSubmit: (name) =>
                    void m.createSpace(name, SPACE_COLORS[ws.spaces.length % SPACE_COLORS.length]!),
                })
              }
            >
              <Plus className="size-3.5" />
            </IconButton>
          }
        >
          Spaces
        </SectionTitle>
        {ws.spaces
          .filter((s) => !s.archived_at)
          .map((s) => (
            <SpaceNode
              key={s.id}
              space={s}
              folders={ws.folders.filter((f) => f.space_id === s.id && !f.archived_at)}
              lists={ws.lists.filter((l) => l.space_id === s.id && !l.archived_at)}
              counts={counts}
              activeListId={activeListId}
              onNavigate={onNavigate}
            />
          ))}
        {ws.loaded && ws.spaces.length === 0 && (
          <p className="px-2 py-2 text-sm text-ink-3">
            No spaces yet. Create one with the + above.
          </p>
        )}
      </nav>
    </aside>
  );
}

function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mt-3 flex h-7 items-center justify-between px-2 text-cap font-semibold text-ink-3">
      <span>{children}</span>
      {action}
    </div>
  );
}

function NavLink({
  to,
  icon,
  label,
  count,
  active,
  onNavigate,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  count?: number;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        itemCls,
        active ? 'bg-primary-soft font-medium text-primary-ink' : 'text-ink hover:bg-surface-2',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <span className="text-ink-2">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count ? <span className="tabular text-cap text-ink-3">{count}</span> : null}
    </Link>
  );
}

function SpaceNode({
  space,
  folders,
  lists,
  counts,
  activeListId,
  onNavigate,
}: {
  space: Space;
  folders: Folder[];
  lists: List[];
  counts: Map<string, number>;
  activeListId?: string;
  onNavigate?: () => void;
}) {
  const expanded = usePrefs((s) => s.expandedSpaces[space.id] ?? true);
  const toggle = usePrefs((s) => s.toggleSpace);
  const total = lists.reduce((n, l) => n + (counts.get(l.id) ?? 0), 0);
  const navigate = useNavigate();
  const folderless = lists.filter((l) => !l.folder_id);
  return (
    <div>
      <div className={cn(itemCls, 'pr-1 hover:bg-surface-2')}>
        <button
          onClick={() => toggle(space.id)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${space.name}`}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="size-3.5 shrink-0 text-ink-3" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-ink-3" />
          )}
          <span
            className="flex size-[18px] shrink-0 items-center justify-center rounded-[5px] text-[11px] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
            style={{ background: space.color ?? '#64748b' }}
          >
            {space.name.charAt(0).toUpperCase()}
          </span>
          <span className="truncate">{space.name}</span>
        </button>
        <span className="tabular text-cap text-ink-3 group-hover/item:hidden">{total || ''}</span>
        <span className="hidden items-center group-focus-within/item:flex group-hover/item:flex">
          <Menu>
            <MenuTrigger asChild>
              <IconButton label={`${space.name} options`} size="sm">
                <MoreHorizontal className="size-3.5" />
              </IconButton>
            </MenuTrigger>
            <MenuContent className="w-52">
              <MenuItem
                icon={<Pencil className="size-4" />}
                onSelect={() =>
                  openDialog({
                    kind: 'name',
                    title: 'Rename space',
                    label: 'Space name',
                    initial: space.name,
                    submit: 'Rename',
                    onSubmit: (name) => m.updateSpace(space.id, { name }),
                  })
                }
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<Palette className="size-4" />}
                onSelect={() =>
                  openDialog({
                    kind: 'appearance',
                    title: 'Space colour',
                    color: space.color,
                    showEmoji: false,
                    onSubmit: ({ color }) =>
                      m.updateSpace(space.id, { color: color ?? space.color ?? '#64748b' }),
                  })
                }
              >
                Colour
              </MenuItem>
              <MenuItem
                icon={<CircleDot className="size-4" />}
                onSelect={() => openStatusEditor({ spaceId: space.id })}
              >
                Task statuses
              </MenuItem>
              <MenuItem
                icon={<Tags className="size-4" />}
                onSelect={() => openTagsEditor(space.id)}
              >
                Tags
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                icon={<Archive className="size-4" />}
                onSelect={() =>
                  void m.updateSpace(space.id, { archived_at: new Date().toISOString() })
                }
              >
                Archive space
              </MenuItem>
              <MenuItem
                icon={<Trash2 className="size-4" />}
                destructive
                onSelect={() =>
                  openDialog({
                    kind: 'confirm',
                    title: `Delete ${space.name}?`,
                    body: 'Its lists and tasks move to Trash for 30 days, where you can restore them.',
                    confirm: 'Delete space',
                    destructive: true,
                    onConfirm: () => m.deleteSpace(space.id),
                  })
                }
              >
                Delete
              </MenuItem>
            </MenuContent>
          </Menu>
          <CreateMenu
            spaceId={space.id}
            onCreatedList={(id) => void navigate({ to: '/l/$listId', params: { listId: id } })}
          />
        </span>
      </div>
      {expanded && (
        <div>
          {folders.map((f) => (
            <FolderNode
              key={f.id}
              folder={f}
              lists={lists.filter((l) => l.folder_id === f.id)}
              counts={counts}
              activeListId={activeListId}
              onNavigate={onNavigate}
            />
          ))}
          {folderless.map((l) => (
            <ListItem
              key={l.id}
              list={l}
              count={counts.get(l.id)}
              active={l.id === activeListId}
              indent={1}
              onNavigate={onNavigate}
            />
          ))}
          {folders.length === 0 && folderless.length === 0 && (
            <p className="py-1 pl-9 text-sm text-ink-3">No lists yet</p>
          )}
        </div>
      )}
    </div>
  );
}

function CreateMenu({
  spaceId,
  folderId,
  onCreatedList,
}: {
  spaceId: string;
  folderId?: string;
  onCreatedList: (id: string) => void;
}) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton label="Create list or folder" size="sm">
          <Plus className="size-3.5" />
        </IconButton>
      </MenuTrigger>
      <MenuContent className="w-44">
        <MenuItem
          icon={<ListPlus className="size-4" />}
          onSelect={() =>
            openDialog({
              kind: 'name',
              title: 'New list',
              label: 'List name',
              submit: 'Create list',
              onSubmit: async (name) =>
                onCreatedList(await m.createList(spaceId, name, { folderId })),
            })
          }
        >
          List
        </MenuItem>
        {!folderId && (
          <MenuItem
            icon={<FolderPlus className="size-4" />}
            onSelect={() =>
              openDialog({
                kind: 'name',
                title: 'New folder',
                label: 'Folder name',
                submit: 'Create folder',
                onSubmit: (name) => void m.createFolder(spaceId, name),
              })
            }
          >
            Folder
          </MenuItem>
        )}
      </MenuContent>
    </Menu>
  );
}

function FolderNode({
  folder,
  lists,
  counts,
  activeListId,
  onNavigate,
}: {
  folder: Folder;
  lists: List[];
  counts: Map<string, number>;
  activeListId?: string;
  onNavigate?: () => void;
}) {
  const expanded = usePrefs((s) => s.expandedFolders[folder.id] ?? true);
  const toggle = usePrefs((s) => s.toggleFolder);
  const navigate = useNavigate();
  return (
    <div>
      <div className={cn(itemCls, 'pr-1 pl-6 hover:bg-surface-2')}>
        <button
          onClick={() => toggle(folder.id)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <FolderIcon className="size-4 shrink-0 text-ink-2" />
          <span className="truncate">{folder.name}</span>
        </button>
        <span className="hidden items-center group-focus-within/item:flex group-hover/item:flex">
          <Menu>
            <MenuTrigger asChild>
              <IconButton label={`${folder.name} options`} size="sm">
                <MoreHorizontal className="size-3.5" />
              </IconButton>
            </MenuTrigger>
            <MenuContent className="w-44">
              <MenuItem
                icon={<Pencil className="size-4" />}
                onSelect={() =>
                  openDialog({
                    kind: 'name',
                    title: 'Rename folder',
                    label: 'Folder name',
                    initial: folder.name,
                    submit: 'Rename',
                    onSubmit: (name) => m.updateFolder(folder.id, { name }),
                  })
                }
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<Trash2 className="size-4" />}
                destructive
                onSelect={() =>
                  openDialog({
                    kind: 'confirm',
                    title: `Delete ${folder.name}?`,
                    body: 'Its lists and tasks move to Trash.',
                    confirm: 'Delete folder',
                    destructive: true,
                    onConfirm: () => m.deleteFolder(folder.id),
                  })
                }
              >
                Delete
              </MenuItem>
            </MenuContent>
          </Menu>
          <CreateMenu
            spaceId={folder.space_id}
            folderId={folder.id}
            onCreatedList={(id) => void navigate({ to: '/l/$listId', params: { listId: id } })}
          />
        </span>
      </div>
      {expanded &&
        lists.map((l) => (
          <ListItem
            key={l.id}
            list={l}
            count={counts.get(l.id)}
            active={l.id === activeListId}
            indent={2}
            onNavigate={onNavigate}
          />
        ))}
    </div>
  );
}

function ListItem({
  list,
  count,
  active,
  indent,
  prefix,
  onNavigate,
}: {
  list: List;
  count?: number;
  active: boolean;
  indent: number;
  prefix?: string;
  onNavigate?: () => void;
}) {
  const ws = useWorkspace();
  const favorites = usePrefs((s) => s.favorites);
  const toggleFavorite = usePrefs((s) => s.toggleFavorite);
  const pushRecent = usePrefs((s) => s.pushRecent);
  const fav = favorites.includes(list.id);
  return (
    <div
      className={cn(
        itemCls,
        'pr-1',
        active ? 'bg-primary-soft font-medium text-primary-ink' : 'hover:bg-surface-2',
      )}
      style={{ paddingLeft: 8 + indent * 22 }}
    >
      <Link
        to="/l/$listId"
        params={{ listId: list.id }}
        onClick={() => {
          pushRecent('list', list.id);
          onNavigate?.();
        }}
        className="flex min-w-0 flex-1 items-center gap-2"
        aria-current={active ? 'page' : undefined}
      >
        <span className="w-4 shrink-0 text-center">
          {list.emoji ?? <ListChecks className="size-4 text-ink-2" />}
        </span>
        <span className="truncate">
          {prefix ? <span className="text-ink-3">{prefix} · </span> : null}
          {list.name}
        </span>
      </Link>
      <span
        className={cn(
          'tabular text-cap group-hover/item:hidden',
          active ? 'text-primary-ink' : 'text-ink-3',
        )}
      >
        {count || ''}
      </span>
      <span className="hidden group-focus-within/item:flex group-hover/item:flex">
        <Menu>
          <MenuTrigger asChild>
            <IconButton label={`${list.name} options`} size="sm">
              <MoreHorizontal className="size-3.5" />
            </IconButton>
          </MenuTrigger>
          <MenuContent className="w-52">
            <MenuItem
              icon={<Pencil className="size-4" />}
              onSelect={() =>
                openDialog({
                  kind: 'name',
                  title: 'Rename list',
                  label: 'List name',
                  initial: list.name,
                  submit: 'Rename',
                  onSubmit: (name) => m.updateList(list.id, { name }),
                })
              }
            >
              Rename
            </MenuItem>
            <MenuItem
              icon={<Palette className="size-4" />}
              onSelect={() =>
                openDialog({
                  kind: 'appearance',
                  title: 'Colour & icon',
                  color: list.color,
                  emoji: list.emoji,
                  showEmoji: true,
                  onSubmit: ({ color, emoji }) => m.updateList(list.id, { color, emoji }),
                })
              }
            >
              Colour & icon
            </MenuItem>
            <MenuItem icon={<Star className="size-4" />} onSelect={() => toggleFavorite(list.id)}>
              {fav ? 'Remove from favorites' : 'Add to favorites'}
            </MenuItem>
            <MenuItem
              icon={<CircleDot className="size-4" />}
              onSelect={() => openStatusEditor({ listId: list.id })}
            >
              Task statuses
            </MenuItem>
            <MenuSub>
              <MenuSubTrigger icon={<FolderInput className="size-4" />}>Move to</MenuSubTrigger>
              <MenuSubContent className="w-52">
                {ws.spaces.map((s) => (
                  <div key={s.id}>
                    <MenuItem
                      disabled={s.id === list.space_id && !list.folder_id}
                      onSelect={() =>
                        void m.updateList(list.id, { space_id: s.id, folder_id: null })
                      }
                    >
                      {s.name}
                    </MenuItem>
                    {ws.folders
                      .filter((f) => f.space_id === s.id)
                      .map((f) => (
                        <MenuItem
                          key={f.id}
                          className="pl-6"
                          disabled={f.id === list.folder_id}
                          onSelect={() =>
                            void m.updateList(list.id, { space_id: s.id, folder_id: f.id })
                          }
                        >
                          {f.name}
                        </MenuItem>
                      ))}
                  </div>
                ))}
                <MenuLabel>Statuses follow the new space unless the list has its own.</MenuLabel>
              </MenuSubContent>
            </MenuSub>
            <MenuSeparator />
            <MenuItem
              icon={<Archive className="size-4" />}
              onSelect={() => void m.updateList(list.id, { archived_at: new Date().toISOString() })}
            >
              Archive
            </MenuItem>
            <MenuItem
              icon={<Trash2 className="size-4" />}
              destructive
              onSelect={() =>
                openDialog({
                  kind: 'confirm',
                  title: `Delete ${list.name}?`,
                  body: 'The list and its tasks move to Trash for 30 days.',
                  confirm: 'Delete list',
                  destructive: true,
                  onConfirm: () => m.deleteList(list.id),
                })
              }
            >
              Delete
            </MenuItem>
          </MenuContent>
        </Menu>
      </span>
    </div>
  );
}
